import { Grid, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { FC, memo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Events } from '../eventsV2/events';
import { GenreType } from '../../core/models/events';
import { useLazyGetEventsByGenreQuery } from '../../core/api/eventsSlice';
import { useRoutesHelper } from '../../helpers/routesHelper';
import { GenreEventsSkeleton } from '../skeletons/genreEventsSkeleton';
import { SkeletonEventCarousel } from '../skeletons/skeletonEventCarousel';
import { useInView } from '../../helpers/customHooks';

const useStyles = makeStyles()(() => ({
  titleButton: {
    display: 'inline-block',
    padding: 0,
    border: 'none',
    background: 'none',
    color: 'inherit',
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    textDecorationColor: 'rgba(255,255,255,0.4)',
    '&:hover': {
      textDecoration: 'underline',
      textDecorationColor: 'rgba(255,255,255,0.8)',
      color: 'rgb(178, 178, 178)',
    },
  },
  gridTitle: {
    fontSize: 14,
    lineHeight: '14px',
    textTransform: 'uppercase',
  },
  placeholder: {
    minHeight: '330px',
    width: '100%',
  },
}));

const PAGE_SIZE = 10;

const LazyGenreSection: FC<LazyGenreSectionType> = memo(({ genre, loadGenreEvents, onTitleClick, classes }) => {
  const { ref, inView } = useInView('400px');

  return (
    <Grid item lg={12} xs={12} sm={12} md={12} xl={12}>
      <div ref={ref}>
        {!inView ? (
          <div className={classes.placeholder}>
            <Link
              component="button"
              underline="none"
              className={classes.titleButton}
              onClick={() => onTitleClick(genre)}
            >
              <Typography variant="h6" className={classes.gridTitle} style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                {genre.title}
              </Typography>
            </Link>
            <SkeletonEventCarousel skeletonCount={3} />
          </div>
        ) : (
          <>
            <Link
              component="button"
              underline="none"
              className={classes.titleButton}
              onClick={() => onTitleClick(genre)}
            >
              <Typography variant="h6" className={classes.gridTitle} style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}>
                {genre.title}
              </Typography>
            </Link>
            <Events load={(skip) => loadGenreEvents(genre.id, skip)} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>
    </Grid>
  );
});

export const GenresEventsCarousel: FC<GenreEventsCarouselType> = memo(({ genres, isLoadingGenres }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { getCategoryEventsRoute } = useRoutesHelper();

  const [getEventsByGenreRequest] = useLazyGetEventsByGenreQuery();

  const loadGenreEvents = useCallback(async (genreId: number, skip = 0) => {
    const { data } = await getEventsByGenreRequest({ genreId, skip, count: PAGE_SIZE });
    return data;
  }, []);

  const handleTitleClick = useCallback((genre: GenreType) => navigate(getCategoryEventsRoute(genre.slug)), [navigate]);

  return (
    <Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
      {isLoadingGenres && <GenreEventsSkeleton />}
      {!isLoadingGenres &&
        genres
          .slice()
          .sort((a, b) => b.eventsCount - a.eventsCount)
          .map((genre) => (
            <LazyGenreSection
              key={genre.id}
              genre={genre}
              loadGenreEvents={loadGenreEvents}
              onTitleClick={handleTitleClick}
              classes={classes}
            />
          ))}
    </Grid>
  );
});

type LazyGenreSectionType = {
  genre: GenreType;
  loadGenreEvents: (genreId: number, skip: number) => Promise<any>;
  onTitleClick: (genre: GenreType) => void;
  classes: any;
};

type GenreEventsCarouselType = {
  genres: GenreType[];
  isLoadingGenres: boolean;
};
